import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  House,
  Package,
  CookingPot,
  Factory,
  ShoppingBag,
  ClockCounterClockwise,
  ChartBar,
  ChatCircleDots,
  Sun,
  Moon,
  List,
  X,
} from '@phosphor-icons/react';
import { Toaster } from './ui/sonner';

const LOGO_URL = '/logo-bumen.png';

const nav = [
  { to: '/', label: 'Dashboard', icon: House },
  { to: '/bahan', label: 'Bahan Baku', icon: Package },
  { to: '/produk', label: 'Produk & Harga', icon: CookingPot },
  { to: '/produksi', label: 'Catat Produksi', icon: Factory },
  { to: '/penjualan', label: 'Catat Penjualan', icon: ShoppingBag },
  { to: '/riwayat', label: 'Riwayat', icon: ClockCounterClockwise },
  { to: '/laporan', label: 'Laporan', icon: ChartBar },
  { to: '/asisten', label: 'Asisten AI', icon: ChatCircleDots },
];

export default function Layout() {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const current = nav.find((n) => n.to === location.pathname) || nav[0];

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border/60 bg-card/50 backdrop-blur sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-border/60">
          <div className="bg-white rounded-xl border border-border/60 px-3 py-2 flex justify-center">
            <img src={LOGO_URL} alt="Bumen Farm" data-testid="brand-logo" className="h-16 w-auto object-contain" />
          </div>
          <div className="mt-3">
            <div className="font-display font-bold text-lg leading-none">Bumen HUB</div>
            <div className="text-xs text-muted-foreground mt-1">UMKM Dashboard</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              <n.icon size={20} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border/60 text-xs text-muted-foreground">
          <div>Hitung HPP · Kelola Stok · Lihat Untung</div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card border-r border-border/60 flex flex-col">
            <div className="px-5 py-6 border-b border-border/60 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg border border-border/60 px-2 py-1"><img src={LOGO_URL} alt="Bumen Farm" className="h-10 w-auto object-contain" /></div>
                <div className="font-display font-bold text-lg">HUB</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="close-mobile-nav"><X size={22} /></button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <n.icon size={20} weight="duotone" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/60">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-secondary"
                onClick={() => setOpen(true)}
                data-testid="open-mobile-nav"
              >
                <List size={22} />
              </button>
              <div className="lg:hidden bg-white rounded-md border border-border/60 px-1.5 py-0.5"><img src={LOGO_URL} alt="Bumen Farm" data-testid="mobile-brand-logo" className="h-8 w-auto object-contain" /></div>
              <div>
                <h1 className="font-display text-xl sm:text-2xl font-semibold leading-none">{current.label}</h1>
                <div className="text-xs text-muted-foreground mt-1 hidden sm:block">Bumen HUB · UMKM Management</div>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-2 rounded-lg border border-border/60 hover:bg-secondary"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </header>

        <main className="px-4 lg:px-8 py-6 lg:py-8 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
